/**
 * @file index.tsx
 *
 * @fileoverview Input component. Renders a button input together with its label.
 */

import { cva, VariantProps } from "class-variance-authority";
import React, { MutableRefObject } from "react";
import CloseIcon from "@jq-icons/Close";
import Button from "./../../01-forms/01-button";
import { FormItem } from "./../../01-forms/11-form-item";
import { FormLabel } from "./../../01-forms/11-form-item/01-form-label";
import { FormDescription } from "./../../01-forms/11-form-item/02-form-description";
import styles from "./input.module.scss";

const inputCva = cva([styles.input], {
  variants: {
    error: {
      true: styles.inputError,
    },
    iconPosition: {
      start: styles.inputIconStart,
      end: styles.inputIconEnd,
    },
  },
  defaultVariants: {
    error: false,
  },
});

const inputIcon = cva([styles.inputIcon], {
  variants: {
    iconPosition: {
      start: styles.iconStart,
      end: styles.iconEnd,
    },
  },
  defaultVariants: {
    iconPosition: "end",
  },
});

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputIcon>,
    VariantProps<typeof inputCva> {
  /**
   * The label to be shown next to the input.
   */
  label?: string | React.ReactNode;
  /**
   * Description for this field.
   */
  description?: string;
  /**
   * Callback for when the input is cleared.
   */
  onClear?: () => void;
  /**
   * The input icon.
   */
  Icon?: React.ReactNode;
}

/**
 * A input is a form element where users can type information on. The input can be of several types (the parent component can set that via the "type" property).
 * The component inherits props from the "InputHTMLAttributes" so any property for a checkbox can be used with this component.
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      label,
      description,
      disabled = false,
      required = false,
      error = false,
      onClear,
      Icon,
      iconPosition = "end",
      className,
      ...props
    },
    ref
  ) => {
    const inputElement = (
      <div className={styles.inputWrapper}>
        <input
          className={`${inputCva({
            error,
            iconPosition: Icon ? iconPosition : null,
          })} ${className}`}
          ref={ref}
          id={id}
          required={required}
          disabled={disabled}
          {...props}
        />
        {onClear &&
          ((ref as MutableRefObject<HTMLInputElement>)?.current as any)
            ?.value && (
            <div className={styles.jclInputClear}>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClear();
                  // @ts-ignore.
                  ref.current.value = "";
                }}
                buttonStyle="primary"
                fab={true}
                raised={false}
                type="button"
              >
                <CloseIcon title="Reset" />
              </Button>
            </div>
          )}
        {Icon && <div className={inputIcon({ iconPosition })}>{Icon}</div>}
      </div>
    );

    if (!label) {
      return <>{inputElement}</>;
    }

    return (
      <FormItem>
        <FormLabel error={error} required={required} htmlFor={id}>
          {label}
        </FormLabel>
        {inputElement}
        {description && <FormDescription>{description}</FormDescription>}
      </FormItem>
    );
  }
);

export default Input;
